// The player's keepsakes, saved in this browser: shells, finds, residents of
// the cove, nights (as stained glass), lanterns. Pure data; no rendering here.
import { DEFAULT_SHELLS } from '../sim/shells.js';

const KEY = 'tsukinoura:profile';
export const DRIFT = ['float', 'wood', 'net', 'seaglass'];
export const LITTLES = ['fry', 'takoko', 'seahorse', 'fugu'];

export function freshProfile() {
  return {
    v: 1,
    shells: { sazae: 1, horagai: 1, takaragai: 1, tsumetagai: 1 },
    loadout: { ...DEFAULT_SHELLS },
    pearls: 0,
    drift: { float: 0, wood: 0, net: 0, seaglass: 0 },
    residents: { fry: 0, takoko: 0, seahorse: 0, fugu: 0 },
    decor: { lantern: 0, netfloat: 0, noren: 0, boat: 0 },
    nights: [],            // newest first: { date, mode, rules, tide, phase, totals, glass }
    beach: {},             // `${date}:${mode}:${n}` -> finds (so a night's beach cannot be rerolled)
    lanterns: [],          // { name, month, at }
    coins: 300,            // prototype currency, no real payment
    almanac: false,        // prototype subscription toggle
    patterns: {},          // shell -> glass pattern id (cosmetic)
    owned: { patterns: [] },
  };
}

export function loadProfile(storage = globalThis.localStorage) {
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return freshProfile();
    return { ...freshProfile(), ...JSON.parse(raw) };
  } catch { return freshProfile(); }
}

export function saveProfile(p, storage = globalThis.localStorage) {
  try { storage?.setItem(KEY, JSON.stringify(p)); } catch { /* private mode or full: keep playing */ }
}

// Record a finished night: residents join the cove, the glass is kept.
export function recordNight(p, { date, mode, plan, totals, history }) {
  for (const h of history) for (const k of LITTLES) p.residents[k] += h.savedKinds?.[k] || 0;
  p.nights.unshift({
    date, mode, game: plan.mode, tide: plan.tide, phase: plan.phase, rules: plan.okite.rules, totals,
    glass: history.map(h => h.glass), pattern: p.patternOn || '', sent: history.flatMap(h => h.sent || []),
  });
  p.nights = p.nights.slice(0, 40);
  return p;
}

export function addFinds(p, finds) {
  for (const f of finds) {
    if (f.type === 'shell') p.shells[f.id] = (p.shells[f.id] || 0) + 1;
    if (f.type === 'pearl') p.pearls += f.n;
    if (f.type === 'drift') p.drift[f.id] += f.n;
  }
  return p;
}

// ---------------------------------------------------------------- the cove

// What each decoration costs in finds. Placing them lights up the cove.
export const RECIPES = {
  lantern: { wood: 2, seaglass: 1 },
  netfloat: { net: 2, float: 2 },
  noren: { seaglass: 2, pearls: 1 },
  boat: { wood: 4, net: 1 },
};
export const DECOR_MAX = { lantern: 8, netfloat: 6, noren: 4, boat: 3 };

export function canBuild(p, id) {
  if (p.decor[id] >= DECOR_MAX[id]) return false;
  return Object.entries(RECIPES[id]).every(([k, n]) => (k === 'pearls' ? p.pearls : p.drift[k]) >= n);
}
export function build(p, id) {
  if (!canBuild(p, id)) return false;
  for (const [k, n] of Object.entries(RECIPES[id])) { if (k === 'pearls') p.pearls -= n; else p.drift[k] -= n; }
  p.decor[id]++;
  return true;
}
// 0..1: how lit the cove is, from decorations and residents.
export function coveLight(p) {
  const d = Object.entries(p.decor).reduce((n, [k, v]) => n + v / DECOR_MAX[k], 0) / 4;
  const r = Math.min(1, Object.values(p.residents).reduce((a, b) => a + b, 0) / 60);
  return Math.min(1, d * 0.7 + r * 0.3);
}

// ---------------------------------------------------------------- shop (prototype)

export const SHOP = {
  lantern: { price: 120 },          // 奉納灯籠: a named lantern for this month
  patternKiriko: { price: 60, pattern: 'kiriko' },   // 切子 cut-glass pattern for a shell
  patternShibori: { price: 60, pattern: 'shibori' }, // 絞り tie-dye pattern
  patternKinran: { price: 90, pattern: 'kinran' },   // 金襴 gold brocade
  almanac: { price: 200 },          // 潮見表, monthly
};

export function offerLantern(p, name, month) {
  if (p.coins < SHOP.lantern.price || !name.trim()) return false;
  if (p.lanterns.some(l => l.month === month)) return false;   // one per month
  p.coins -= SHOP.lantern.price;
  p.lanterns.push({ name: name.trim().slice(0, 12), month, at: Date.now() });
  return true;
}
export function buyPattern(p, itemId) {
  const it = SHOP[itemId];
  if (!it?.pattern || p.coins < it.price || p.owned.patterns.includes(it.pattern)) return false;
  p.coins -= it.price;
  p.owned.patterns.push(it.pattern);
  return true;
}
export function buyAlmanac(p) {
  if (p.almanac || p.coins < SHOP.almanac.price) return false;
  p.coins -= SHOP.almanac.price;
  p.almanac = true;
  return true;
}
